import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { EMPTY, Observable, catchError, combineLatest, expand, map, of, reduce, startWith, switchMap } from 'rxjs';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { MemberFollowUpApiService } from '@core/api/services/member-follow-up-api.service';
import { SpiritualAssistanceVisitApiService } from '@core/api/services/spiritual-assistance-visit-api.service';
import { DivineServiceApiService } from '@core/api/services/divine-service-api.service';
import { UnitService } from '../services/unit.service';
import { MemberRow } from '../models/unit.model';
import { getUpcomingBirthdays, summarizeCommunion } from './unit-start-summary';

function cardState<T>(source: Observable<T>) {
  return source.pipe(
    map(data => ({ data, loading: false, error: false })),
    startWith({ data: null, loading: true, error: false }),
    catchError(() => of({ data: null, loading: false, error: true })),
  );
}

@Component({
  selector: 'app-unit-start-page',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule],
  templateUrl: './unit-start-page.component.html',
  styleUrl: './unit-start-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitStartPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly units = inject(UnitService);
  private readonly userInfo = inject(UserFullInfoService);
  private readonly followUp = inject(MemberFollowUpApiService);
  private readonly visits = inject(SpiritualAssistanceVisitApiService);
  private readonly divineServices = inject(DivineServiceApiService);

  readonly vm$ = combineLatest([
    this.route.parent!.parent!.paramMap,
    this.userInfo.getUserInfo$(),
  ]).pipe(switchMap(([params, user]) => {
    const unitId = params.get('unit-id')!;
    const canView = this.userInfo.hasActivitySnapshot(user) && !!(user?.isAdmin ||
      user?.readableUnitIds.some(id => id.toLowerCase() === unitId.toLowerCase()));
    if (!canView) return of({ unitId, canView: false, cards: null });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const from = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    const agendaStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    const communion$ = this.followUp.get(unitId, { page: 1, pageSize: 100, isHomeAttended: true }).pipe(
        expand(page => page.pageNumber < page.totalPages
          ? this.followUp.get(unitId, { page: page.pageNumber + 1, pageSize: 100, isHomeAttended: true })
          : EMPTY),
        map(page => summarizeCommunion(page.items, today)),
        reduce((total, page) => ({ total: total.total + page.total, pending: total.pending + page.pending }), { total: 0, pending: 0 }),
      );

    return combineLatest({
      members: cardState(this.units.getMembersFull(unitId).pipe(map(members => ({
        birthdays: getUpcomingBirthdays(members, today),
        groups: [
          { label: 'Activos', count: members.filter(member => member.status === 'Active').length },
          { label: 'Atendidos en el hogar', count: members.filter(member => member.isHomeAttended).length },
          { label: 'Inactivos', count: members.filter(member => member.status === 'Inactive').length },
        ],
      })))),
      services: cardState(this.units.getLatestDivineServices(unitId, 2, today)),
      agenda: cardState(this.divineServices.getDivineServices(unitId, agendaStart, from, 'date', 'asc', 1, 2).pipe(
        map(response => response.items ?? []))),
      communion: cardState(communion$),
      visits: cardState(this.userInfo.hasPermission('ManageSpiritualAssistanceVisits', user)
        ? this.visits.list(unitId, monthStart, agendaStart).pipe(map(visits => ({
          visitedMemberCount: new Set(visits.flatMap(visit => visit.recipients.map(recipient => recipient.id))).size,
        })))
        : of(null)),
    }).pipe(map(cards => ({ unitId, canView: true, cards })));
  }));

  memberName(member: MemberRow): string {
    return `${member.firstName} ${member.lastName}`.trim();
  }
}
